import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Flex, Text} from '@mantine/core';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {AnalyticsEvent, trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  requireScientificModel,
  ScientificModelsByExternalReference,
  type ScientificModelNode,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {UserVerificationStatusQuery} from 'model-hub/scientific-models/structure-prediction/__generated/UserVerificationStatusQuery';
import {AlphaFoldPaperLink} from 'model-hub/scientific-models/structure-prediction/AlphaFoldPaperLink';
import {StructurePredictionHeader} from 'model-hub/scientific-models/structure-prediction/StructurePredictionHeader';
import {
  ACADEMIC_VERIFICATION_MESSAGE,
  AvailableStructureInfo,
  getRunBlockingMessage,
  isAcademicUserBlocked,
  USER_VERIFICATION_STATUS,
} from 'model-hub/scientific-models/structure-prediction/StructurePredictionUtils';
import {useRefreshOnExpiredUrl} from 'model-hub/scientific-models/structure-prediction/useRefreshOnExpiredUrl';
import MolstarViewer, {MOLSTAR_PREVIEW_TYPE_TO_FORMAT} from 'molbio/molstar/ViewerWrapper';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {CenteredSpinner} from 'ui-library/Spinner';

function ModelCitation({className, model}: {className?: string; model: ScientificModelNode}): JSX.Element {
  return (
    <Text component="p" className={className}>
      3D structures are predicted using&nbsp;
      <AlphaFoldPaperLink model={model} />.
    </Text>
  );
}

export function StructureViewerPane({
  structureInfo,
  predictionInProgress,
  primaryEntityId,
  aminoAcids,
  permissions,
  interactionAnalyticsEvent,
  currentAaSequenceSequenceAminoAcids,
  selectionStart,
  selectionEnd,
  catalogByExternalReference,
  onBack,
  redoPrediction,
  onRefreshExpiredUrl,
  testComponent,
}: {
  structureInfo: AvailableStructureInfo;
  predictionInProgress: boolean;
  primaryEntityId: string;
  aminoAcids: string;
  permissions: Permissions;
  interactionAnalyticsEvent: AnalyticsEvent;
  currentAaSequenceSequenceAminoAcids?: string;
  selectionStart?: number;
  selectionEnd?: number | null;
  catalogByExternalReference: ScientificModelsByExternalReference;
  onBack: () => void;
  redoPrediction?: () => void;
  /**
   * When provided, `structureInfo.structureFile.url` is treated as an already-signed download URL
   * (e.g. a prediction job's standardized output) and rendered directly; the callback refetches a
   * fresh URL once the current one expires. When omitted, the URL is re-signed on demand via
   * SignedExtFileURLProxy using `structureFile.id` and `versionId` (the external-file flow).
   */
  onRefreshExpiredUrl?: () => void;
  testComponent: string;
}): JSX.Element {
  const {classes} = useStyles();
  const userVerificationStatusQueryResult =
    useBenchlingQuery<UserVerificationStatusQuery>(USER_VERIFICATION_STATUS);
  if (userVerificationStatusQueryResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  } else if (userVerificationStatusQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Error when loading structure prediction details. Please refresh the page.');
    return <></>;
  }

  const model = requireScientificModel(catalogByExternalReference, structureInfo.model);

  if (structureInfo.structureFile == null) {
    return (
      <div className={classes.smallTabSpacing}>
        <Text component="p" className={classes.largestText}>
          Predicting 3D structure...
        </Text>
        <Text component="p" className={classes.smallerText}>
          This could take some time. You will receive an email when it’s ready.
        </Text>
        <ModelCitation className={classes.smallerText} model={model} />
      </div>
    );
  } else {
    const currentUser = userVerificationStatusQueryResult.data.currentUser;
    assert(
      currentUser?.__typename === 'UserNode' || currentUser == null,
      `Non-user should not access this page.`
    );

    let warningMessage: ReactNode | null = null;
    if (structureInfo.structureFileOutOfDate) {
      warningMessage = 'This structure is out of sync.';
      const runBlockingMessage = getRunBlockingMessage(aminoAcids, permissions, 1, model);
      if (predictionInProgress) {
        // NOTE: this message doesn't handle the case where a different user requested the run.
        warningMessage = `${warningMessage} You’ll receive an email when your updated structure is ready.`;
      } else if (runBlockingMessage) {
        warningMessage = `${warningMessage} ${runBlockingMessage}`;
      } else if (isAcademicUserBlocked(currentUser)) {
        warningMessage = `${warningMessage} ${ACADEMIC_VERIFICATION_MESSAGE}`;
      } else if (redoPrediction != null) {
        warningMessage = (
          <span>
            {warningMessage}{' '}
            <Button
              buttonStyle={ButtonStyle.LINK}
              className={classes.buttonLinkText}
              text="Redo structure prediction"
              onClick={redoPrediction}
            />
            .
          </span>
        );
      }
    }

    const renderViewer = (viewerURL: string): JSX.Element => (
      <MolstarViewer
        url={viewerURL}
        format={MOLSTAR_PREVIEW_TYPE_TO_FORMAT[structureInfo.structureFile.previewType]}
        className={classNames('reset', classes.viewer)}
        interactionAnalyticsCallback={() => {
          trackEvent(interactionAnalyticsEvent);
        }}
        residueStart={selectionStart}
        residueEnd={selectionEnd}
        disableSelection={structureInfo.structureFileOutOfDate}
        currentAaSequenceSequence={currentAaSequenceSequenceAminoAcids}
      />
    );

    return (
      <Flex
        direction="column"
        h="100%"
        {...getTestProps({component: testComponent, element: 'viewerWrapper'})}
      >
        <StructurePredictionHeader
          testComponent={testComponent}
          onBack={onBack}
          componentIds={structureInfo.componentIds}
        />
        {warningMessage && (
          <div className={classes.smallInlineBannerMargin}>
            <InlineBanner text={warningMessage} bannerType={InlineBannerType.WARNING} />
          </div>
        )}
        {onRefreshExpiredUrl != null ? (
          <PreSignedStructureViewer
            url={structureInfo.structureFile.url}
            onRefreshExpiredUrl={onRefreshExpiredUrl}
            renderViewer={renderViewer}
          />
        ) : (
          <SignedExtFileURLProxy
            url={structureInfo.structureFile.url}
            externalFileId={structureInfo.structureFile.id}
            owningEntityApiId={primaryEntityId}
            versionId={structureInfo.versionId}
            renderWithSignedURL={(signedURL: string | null | undefined) => {
              if (signedURL === undefined) {
                return <CenteredSpinner />;
              } else if (signedURL == null) {
                return (
                  <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading structure file." />
                );
              } else {
                return renderViewer(signedURL);
              }
            }}
          />
        )}
      </Flex>
    );
  }
}

/**
 * Renders a structure whose URL is already a signed download link (e.g. a prediction job's
 * standardized output). Signed S3 URLs expire, so once the current one lapses we refetch the owning
 * resource to mint a fresh URL rather than handing the viewer a dead link.
 */
function PreSignedStructureViewer({
  url,
  onRefreshExpiredUrl,
  renderViewer,
}: {
  url: string;
  onRefreshExpiredUrl: () => void;
  renderViewer: (viewerURL: string) => JSX.Element;
}): JSX.Element {
  const expired = useRefreshOnExpiredUrl(url, onRefreshExpiredUrl);
  if (expired) {
    return <CenteredSpinner />;
  }
  return renderViewer(url);
}

const useStyles = createStyles((theme) => ({
  smallTabSpacing: {
    margin: theme.spacing.lg,
  },
  largestText: FONT_PROXIMA_17,
  smallerText: FONT_PROXIMA_13,
  buttonLinkText: {
    verticalAlign: 'baseline',
  },
  viewer: {
    borderWidth: '0px',
    flex: '1 1 auto',
  },
  smallInlineBannerMargin: {
    margin: theme.spacing.xs,
  },
}));
